import React from 'react';
import { Button } from '@mui/material';
import { sendAnalyticsEventToServer, trackGAEvent } from '../Utils/analytics';

/* mandaory props for custom button:  title, onClick  */

export default function CustomButton({
  title = 'Submit', // default label if not passed
  onClick, // click handler
  customStyles = {}, // allow consumers style to override sx styles
  size = 'rg', // button size: small | rg | lg
  setDisable = false, // disable state
  type = 'primary', // button type: primary | secondary | tertiary | destructive | destructive-outline
  bgColor, // background color
  hoverBgColor, // hover background color
  activeBoxShadow, // active box shadow
  borderColor, // border color
  hoverBorderColor, // hover border color
  disabledBgColor, // disabled background color
  disabledTextColor, // disabled text color
  disabledBorderColor, // disabled border color
  icon = null, // optional icon element
  iconPosition = 'start', // icon position: 'start' or 'end'
  iconOnly = false, // if true, button renders only the icon (no text)
  fontColor, // font color
  onlyText = false, // onlyText = text button with no background
  category = 'user', // analytics category
  trackEvent = '', // analytics event name
  ...props // spread extra props to MUI Button
}) {
  // Default configurations for each button type
  const typeConfig = {
    primary: {
      bgColor: 'rgba(115, 46, 185, 1)',
      hoverBgColor: 'rgba(96, 13, 180, 1)',
      activeBoxShadow: '0px 0px 0px 2px rgba(247, 240, 255, 1)',
      borderColor: 'transparent',
      hoverBorderColor: 'rgba(96, 13, 180, 1)',
      disabledBgColor: 'rgba(230, 218, 242, 1)',
      disabledTextColor: 'rgba(252, 250, 255, 1)',
      disabledBorderColor: 'rgba(230, 218, 242, 1)',
      fontColor: 'rgba(255, 255, 255, 1)',
    },
    secondary: {
      bgColor: 'rgba(252, 250, 255, 1)',
      hoverBgColor: 'rgba(247, 240, 255, 1)',
      activeBoxShadow: '0px 0px 0px 2px rgba(247, 240, 255, 1)',
      borderColor: 'rgba(96, 13, 180, 1)',
      hoverBorderColor: 'rgba(96, 13, 180, 1)',
      disabledBgColor: 'rgba(252, 250, 255, 1)',
      disabledTextColor: 'rgba(230, 218, 242, 1)',
      disabledBorderColor: 'rgba(230, 218, 242, 1)',
      fontColor: 'rgba(115, 46, 185, 1)',
    },
    tertiary: {
      bgColor: 'rgba(255, 255, 255, 1)',
      hoverBgColor: 'rgba(245, 247, 250, 1)',
      activeBoxShadow: '0px 0px 0px 2px rgba(245, 247, 250, 1)',
      borderColor: 'rgba(213, 220, 229, 1)',
      hoverBorderColor: 'rgba(213, 220, 229, 1)',
      disabledBgColor: 'rgba(249, 250, 252, 1)',
      disabledTextColor: 'rgba(213, 220, 229, 1)',
      disabledBorderColor: 'rgba(228, 233, 240, 1)',
      fontColor: 'rgba(27, 37, 56, 1)',
    },
    destructive: {
      bgColor: 'rgba(217, 61, 66, 1)',
      hoverBgColor: 'rgba(134, 10, 14, 1)',
      activeBoxShadow: '0px 0px 0px 2px rgba(249, 198, 198, 1)',
      borderColor: 'transparent',
      hoverBorderColor: 'rgba(134, 10, 14, 1)',
      disabledBgColor: 'rgba(255, 239, 239, 1)',
      disabledTextColor: 'rgba(255, 255, 255, 1)',
      disabledBorderColor: 'transparent',
      fontColor: 'rgba(255, 255, 255, 1)',
    },
    'destructive-outline': {
      bgColor: 'rgba(255, 255, 255, 1)',
      hoverBgColor: 'rgba(255, 239, 239, 1)',
      activeBoxShadow: 'none',
      borderColor: 'rgba(211, 47, 47, 1)',
      hoverBorderColor: 'rgba(183, 28, 28, 1)',
      disabledBgColor: 'rgba(255, 255, 255, 1)',
      disabledTextColor: 'rgba(255, 205, 210, 1)',
      disabledBorderColor: 'rgba(249, 198, 198, 1)',
      fontColor: 'rgba(211, 47, 47, 1)',
    },
  };

  // Size configurations
  const sizeConfig = {
    small: {
      minWidth: iconOnly ? '26px' : '58px',
      horizontalPadding: iconOnly ? '6px' : '10px',
      verticalPadding: iconOnly ? '6px' : '6px',
      borderRadius: iconOnly ? '6px' : '6px',
      fontSize: '12px',
      iconSize: '16px',
      gap: '2px',
      height: iconOnly ? '26px' : '28px',
    },
    rg: {
      minWidth: iconOnly ? '32px' : '68px',
      horizontalPadding: iconOnly ? '8px' : '14px',
      verticalPadding: iconOnly ? '8px' : '10px',
      borderRadius: iconOnly ? '6px' : '8px',
      fontSize: '14px',
      iconSize: '18px',
      gap: '4px',
      height: iconOnly ? '32px' : '40px',
    },
    lg: {
      minWidth: iconOnly ? '40px' : '72px',
      horizontalPadding: iconOnly ? '10px' : '12px',
      verticalPadding: iconOnly ? '10px' : '8px',
      borderRadius: '8px',
      fontSize: '14px',
      iconSize: '20px',
      gap: '4px',
      height: iconOnly ? '40px' : '44px',
    },
  };

  // Pick correct config based on props

  const currentType = typeConfig[type] || typeConfig.primary;
  const currentSize = sizeConfig[size] || sizeConfig.rg;

  // Use provided props or fall back to type defaults
  const styles = {
    bgColor: bgColor || currentType.bgColor,
    hoverBgColor: hoverBgColor || currentType.hoverBgColor,
    activeBoxShadow: activeBoxShadow || currentType.activeBoxShadow,
    borderColor: borderColor || currentType.borderColor,
    hoverBorderColor: hoverBorderColor || currentType.hoverBorderColor,
    disabledBgColor: disabledBgColor || currentType.disabledBgColor,
    disabledTextColor: disabledTextColor || currentType.disabledTextColor,
    disabledBorderColor: disabledBorderColor || currentType.disabledBorderColor,

    fontColor: fontColor || currentType.fontColor,
    onlyTextColor: currentType.onlyTextColor,
    onlyTextHoverColor: currentType.onlyTextHoverColor,
    onlyTextDisabledColor: currentType.onlyTextDisabledColor,
  };

  //  Clone icon with enforced sizing
  const renderedIcon =
    icon &&
    React.cloneElement(icon, {
      sx: {
        fontSize: currentSize.iconSize,
        width: currentSize.iconSize,
        height: currentSize.iconSize,
      },
    });

  return (
    <Button
      variant="contained"
      disabled={setDisable}
      onClick={() => {
        // Track analytics on every click
        trackGAEvent({
          category,
          action: title,
          label: `${title} clicked in ${window.location.href}`,
          value: trackEvent,
        });
        trackEvent && sendAnalyticsEventToServer(trackEvent);
        onClick();
      }}
      sx={{
        minWidth: currentSize.minWidth,
        padding: `${currentSize.verticalPadding} ${currentSize.horizontalPadding}`,
        fontSize: onlyText ? '14px' : currentSize.fontSize,
        textTransform: 'capitalize',
        boxShadow: 'none',
        borderRadius: currentSize.borderRadius,
        transition: 'none',
        background: onlyText ? 'none' : styles.bgColor,
        border: onlyText
          ? 'none'
          : `1px solid ${
              setDisable ? styles.disabledBorderColor : styles.borderColor
            }`,
        whiteSpace: 'nowrap',
        gap: currentSize.gap,
        flexDirection: iconPosition === 'start' ? 'row' : 'row-reverse', // Set flex direction based on icon position either start or end
        color: onlyText
          ? fontColor || 'rgba(96, 13, 180, 1)'
          : styles.fontColor,
        height: currentSize.height,
        cursor: setDisable ? 'not-allowed' : 'pointer',
        '&:hover': {
          background: onlyText ? 'none' : styles.hoverBgColor,
          border: onlyText ? 'none' : `1px solid ${styles.hoverBorderColor}`,
          boxShadow: onlyText ? 'none' : 'none',
          color: onlyText
            ? fontColor || 'rgba(45, 3, 88, 1)'
            : styles.fontColor,

          cursor: setDisable ? 'not-allowed' : 'pointer',
        },
        '&:active': {
          background: onlyText ? 'none' : styles.hoverBgColor,
          border: onlyText ? 'none' : `1px solid ${styles.hoverBorderColor}`,
          boxShadow: onlyText ? 'none' : styles.activeBoxShadow,
          color: onlyText ? 'rgba(45, 3, 88, 1)' : styles.fontColor,
        },
        '&.Mui-disabled': {
          background: onlyText ? 'none' : styles.disabledBgColor,
          color: onlyText ? 'rgba(230, 218, 242, 1)' : styles.disabledTextColor,
          cursor: 'not-allowed',
        },
        ...customStyles,
      }}
      {...props}
    >
      {renderedIcon}

      {/* Show text unless it's an icon-only button */}
      {iconOnly ? null : title}
    </Button>
  );
}
